import React, { useEffect, useState } from 'react';
import SessionManager from '../utils/sessionManager';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { API_URL } from '../config';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const InterviewReviewPage = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Check if this is a live interview session
    if (interviewId.startsWith('app-') || interviewId.startsWith('session-')) {
      // Redirect to live interview report page
      navigate(`/hr/live-interview-report/${interviewId}`, { replace: true });
    } else {
      // Regular interview - fetch report
      fetchInterviewReport();
    }
  }, [interviewId, navigate]);

  const fetchInterviewReport = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/interview/report/${interviewId}`, { headers: buildAuthHeaders() });
      
      if (!response.ok) {
        throw new Error('Failed to fetch interview report');
      }
      
      const data = await response.json();
      setReportData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading interview report...</p>
        </div>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
        <div className="text-center">
          <Icon name="error" size={48} className="text-red-500 mx-auto mb-4" />
          <p className="text-red-500 mb-4">{error || 'Interview report not found'}</p>
          <Button onClick={() => navigate('/hr-dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const { interview, candidate, job, scoring_result, questions_and_answers, proctoring_events } = reportData;
  const overallPercentage = scoring_result?.overall_percentage || 0;
  const suitabilityScore = scoring_result?.suitability_score || 0;
  
  // Calculate performance categories
  const goodCount = questions_and_answers?.filter(qa => (qa.score || 0) >= 0.8).length || 0;
  const averageCount = questions_and_answers?.filter(qa => (qa.score || 0) >= 0.4 && (qa.score || 0) < 0.8).length || 0;
  const belowAvgCount = questions_and_answers?.filter(qa => (qa.score || 0) < 0.4).length || 0;

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-500';
    if (score >= 0.4) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 0.8) return 'bg-green-500';
    if (score >= 0.4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-text-light-primary dark:text-text-dark-primary">
            Candidate Assessment Report
          </h1>
          <Button variant="outline" onClick={() => navigate('/hr-dashboard')}>
            <Icon name="arrow_back" size={20} className="mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Candidate Profile Card */}
        <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Profile Info */}
            <div className="md:col-span-2">
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="person" size={48} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-text-light-primary dark:text-text-dark-primary mb-2">
                    {candidate?.full_name || 'Unknown Candidate'}
                  </h2>
                  <div className="space-y-1 text-text-light-secondary dark:text-text-dark-secondary">
                    <p><strong>Position:</strong> {job?.title || 'N/A'}</p>
                    <p><strong>Email:</strong> {candidate?.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {candidate?.phone || 'N/A'}</p>
                    <p><strong>Assessment Date:</strong> {new Date(interview?.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Overall Score Circle */}
            <div className="flex flex-col items-center justify-center">
              <h3 className="text-lg font-semibold text-text-light-primary dark:text-text-dark-primary mb-4">
                Overall Score
              </h3>
              <div className="relative w-40 h-40">
                <svg className="transform -rotate-90 w-40 h-40">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="transparent"
                    className="text-gray-300 dark:text-gray-700"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="transparent"
                    strokeDasharray={`${2 * Math.PI * 70}`}
                    strokeDashoffset={`${2 * Math.PI * 70 * (1 - overallPercentage / 100)}`}
                    className={overallPercentage >= 70 ? 'text-green-500' : overallPercentage >= 50 ? 'text-yellow-500' : 'text-red-500'}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-text-light-primary dark:text-text-dark-primary">
                    {overallPercentage}%
                  </span>
                  <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                    {scoring_result?.overall_score?.toFixed(1)}/{questions_and_answers?.length || 5}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6 border-l-4 border-green-500">
            <h3 className="text-green-500 text-sm font-semibold mb-2">Good Performance</h3>
            <p className="text-3xl font-bold text-text-light-primary dark:text-text-dark-primary">{goodCount}</p>
            <p className="text-xs text-text-light-secondary dark:text-text-dark-secondary">Questions (80-100%)</p>
          </div>
          
          <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6 border-l-4 border-yellow-500">
            <h3 className="text-yellow-500 text-sm font-semibold mb-2">Average Performance</h3>
            <p className="text-3xl font-bold text-text-light-primary dark:text-text-dark-primary">{averageCount}</p>
            <p className="text-xs text-text-light-secondary dark:text-text-dark-secondary">Questions (40-79%)</p>
          </div>
          
          <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6 border-l-4 border-red-500">
            <h3 className="text-red-500 text-sm font-semibold mb-2">Below Average</h3>
            <p className="text-3xl font-bold text-text-light-primary dark:text-text-dark-primary">{belowAvgCount}</p>
            <p className="text-xs text-text-light-secondary dark:text-text-dark-secondary">Questions (0-40%)</p>
          </div>
          
          <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6 border-l-4 border-blue-500">
            <h3 className="text-blue-500 text-sm font-semibold mb-2">Total Warnings</h3>
            <p className="text-3xl font-bold text-text-light-primary dark:text-text-dark-primary">
              {interview?.total_warnings || 0}
            </p>
            <p className="text-xs text-text-light-secondary dark:text-text-dark-secondary">Proctoring Alerts</p>
          </div>
        </div>

        {/* Question-wise Performance */}
        <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6 mb-6">
          <h3 className="text-xl font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
            Question-wise Performance
          </h3>
          <div className="space-y-4">
            {questions_and_answers?.map((qa, index) => (
              <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs font-semibold">
                        Q{index + 1}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        qa.difficulty === 'easy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        qa.difficulty === 'hard' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {qa.difficulty?.toUpperCase() || 'NORMAL'}
                      </span>
                    </div>
                    <p className="text-text-light-primary dark:text-text-dark-primary font-medium mb-2">
                      {qa.question}
                    </p>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary mb-2">
                      <strong>Answer:</strong> {qa.answer?.substring(0, 200)}{qa.answer?.length > 200 ? '...' : ''}
                    </p>
                    {qa.feedback && (
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        <strong>Feedback:</strong> {qa.feedback}
                      </p>
                    )}
                    {qa.video_url && (
                      <div className="mt-2">
                        <video controls className="max-w-md rounded border border-gray-300 dark:border-gray-600">
                          <source src={qa.video_url.startsWith('http') ? qa.video_url : `${API_URL.replace('/api', '')}${qa.video_url.startsWith('/') ? '' : '/'}${qa.video_url}`} type="video/webm" />
                          Your browser does not support video playback.
                        </video>
                        <p className="text-xs text-text-light-secondary dark:text-text-dark-secondary mt-1">
                          Answer Recording
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <div className={`text-4xl font-bold ${getScoreColor(qa.score || 0)}`}>
                      {((qa.score || 0) * 5).toFixed(1)}/5
                    </div>
                    <div className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                      {((qa.score || 0) * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${getScoreBgColor(qa.score || 0)}`}
                    style={{ width: `${(qa.score || 0) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Overall Feedback & Suitability */}
        <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6 mb-6">
          <h3 className="text-xl font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
            Overall Assessment & Suitability
          </h3>
          {(scoring_result?.comprehensive_feedback || scoring_result?.hr_summary) ? (
            <>
              <div className="mb-4">
                <h4 className="text-blue-500 font-semibold mb-2">AI-Generated Feedback:</h4>
                <p className="text-text-light-secondary dark:text-text-dark-secondary leading-relaxed whitespace-pre-wrap">
                  {scoring_result.comprehensive_feedback || scoring_result.hr_summary}
                </p>
              </div>
              
              {suitabilityScore > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-text-light-primary dark:text-text-dark-primary mb-2">
                    Suitability Score: 
                    <span className={`ml-2 px-3 py-1 rounded text-lg ${
                      suitabilityScore >= 70 ? 'bg-green-500' :
                      suitabilityScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    } text-white`}>
                      {suitabilityScore}/100
                    </span>
                  </h4>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6">
                    <div
                      className={`h-6 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                        suitabilityScore >= 70 ? 'bg-green-500' :
                        suitabilityScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${suitabilityScore}%` }}
                    >
                      {suitabilityScore}%
                    </div>
                  </div>
                </div>
              )}

              {/* Strengths and Areas for Improvement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {scoring_result.strengths && scoring_result.strengths.length > 0 && (
                  <div>
                    <h4 className="text-green-500 font-semibold mb-2 flex items-center gap-2">
                      <Icon name="check_circle" size={20} />
                      Strengths
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-text-light-secondary dark:text-text-dark-secondary">
                      {scoring_result.strengths.map((strength, idx) => (
                        <li key={idx}>{strength}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {scoring_result.areas_for_improvement && scoring_result.areas_for_improvement.length > 0 && (
                  <div>
                    <h4 className="text-yellow-500 font-semibold mb-2 flex items-center gap-2">
                      <Icon name="lightbulb" size={20} />
                      Areas for Improvement
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-text-light-secondary dark:text-text-dark-secondary">
                      {scoring_result.areas_for_improvement.map((area, idx) => (
                        <li key={idx}>{area}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Recommendation */}
              {scoring_result.recommendation && (
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="text-blue-600 dark:text-blue-400 font-semibold mb-2">Recommendation:</h4>
                  <p className="text-text-light-primary dark:text-text-dark-primary">
                    {scoring_result.recommendation}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <Icon name="info" size={20} className="inline mr-2 text-blue-500" />
              <span className="text-text-light-secondary dark:text-text-dark-secondary">
                Overall feedback is being generated. Please refresh the page in a moment.
              </span>
            </div>
          )}
        </div>

        {/* Proctoring Warnings */}
        {proctoring_events && proctoring_events.length > 0 && (
          <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
            <h3 className="text-xl font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
              Proctoring Warnings
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold">#</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Question</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Type</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Severity</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {proctoring_events.map((event, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 text-sm">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm">Q{event.question_number}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">
                          {event.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          event.severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {event.severity?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-light-secondary dark:text-text-dark-secondary">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewReviewPage;
